export const SITE_MAP = {
  patient: {
    key: 'patient',
    label: 'Patients',
    icon: 'fa-user',
    color: '#EC4899',
    tagline: 'A guided, reassuring journey through perimenopause and menopause care.',
    summary:
      'Patients use Auralis on their phone or desktop to understand their symptoms, track how they feel, see their blood results once a clinician has reviewed them, and book appointments — all in one place, with clinical oversight at every step.',
    demo: { email: 'demo@auralis.health', label: 'Open the patient demo' },
    video: '/sitemap/videos/patient.webm',
    captions: '/sitemap/videos/patient.vtt',
    steps: [
      {
        title: 'Sign in & privacy consent',
        icon: 'fa-right-to-bracket',
        what: 'The patient signs in with their email. On first use they complete a short onboarding and give explicit consent for how their data is used (UK GDPR). Consent can be reviewed or withdrawn at any time from their profile.',
        why: 'Trust is the foundation. Patients see exactly what is collected and why before anything is stored.',
        route: '/app/profile',
        image: '/sitemap/patient-01-signin.jpg',
      },
      {
        title: 'Precision health assessment',
        icon: 'fa-wand-magic-sparkles',
        what: 'A structured questionnaire covers four domains — hormonal transition, metabolic strain, sleep & recovery and mental wellbeing. Auralis scores each domain and identifies the dominant pattern driving the patient’s symptoms.',
        why: 'Replaces the vague “how are you feeling?” with a consistent, repeatable baseline that clinicians can act on.',
        route: '/assessment',
        image: '/sitemap/patient-02-assessment.jpg',
      },
      {
        title: 'Daily check-ins & tracking',
        icon: 'fa-pen-to-square',
        what: 'Quick daily check-ins capture mood, energy, sleep and stress. Weight and BMI can be logged too. Everything feeds trend charts so patterns become visible over weeks, not just at appointments.',
        why: 'Symptoms fluctuate. Longitudinal data shows what is really happening between visits.',
        route: '/app/track',
        image: '/sitemap/patient-03-track.jpg',
      },
      {
        title: '“How I generally feel” — in their own words',
        icon: 'fa-comment-heart',
        what: 'A free-text box (up to 1,000 characters) where the patient describes how they are generally feeling in their own words. Clinicians read this before every review.',
        why: 'Numbers miss nuance. This gives the patient a voice and gives the clinician context no score can provide.',
        route: '/app/profile',
        image: '/sitemap/patient-04-wellbeing.jpg',
      },
      {
        title: 'Blood results — released by the clinician',
        icon: 'fa-vial',
        what: 'Patients cannot enter or edit results. Results appear only after a clinician has reviewed and released them, optionally with a reassuring note. Clinicians can hold back individual values to discuss in person. A plain-English AI analysis sits alongside.',
        why: 'Prevents patients being alarmed by raw numbers out of context, while still giving full transparency once a clinician has framed them.',
        route: '/biomarkers',
        image: '/sitemap/patient-05-results.jpg',
      },
      {
        title: 'Book a consultation',
        icon: 'fa-calendar-check',
        what: 'The patient picks from dates and times their clinicians have actually published, chooses a service tier, and sends a request. The booking shows as “Awaiting approval” until the clinician confirms it.',
        why: 'No double-booking, no back-and-forth emails. Clinicians stay in control of their diary.',
        route: '/app/book',
        image: '/sitemap/patient-06-book.jpg',
      },
      {
        title: 'Messages, reminders & insights',
        icon: 'fa-bell',
        what: 'Clinicians can message patients directly and set follow-up reminders. The Insights page turns tracked data into personalised, AI-generated wellbeing guidance, and a PDF “meeting pack” summarises everything for appointments.',
        why: 'Keeps the patient supported and informed between appointments, and makes every appointment more productive.',
        route: '/app/insights',
        image: '/sitemap/patient-07-insights.jpg',
      },
      {
        title: 'Profile, photo & data rights',
        icon: 'fa-id-badge',
        what: 'Patients can add a profile photo (visible to their clinician for easy identification), update their details, manage consent, and request deletion of their account and data.',
        why: 'Puts the patient in control of their identity and their data, in line with UK GDPR.',
        route: '/app/profile',
        image: '/sitemap/patient-08-profile.jpg',
      },
    ],
  },
  clinician: {
    key: 'clinician',
    label: 'Clinicians',
    icon: 'fa-user-doctor',
    color: '#A855F7',
    tagline: 'Triage, review and release — with AI doing the typing, not the deciding.',
    summary:
      'GPs and specialist clinicians use the clinician portal to manage their caseload, upload and verify lab results with AI assistance, decide exactly what each patient sees, record clinical decisions, and manage their appointment diary.',
    demo: { email: 'ellie@auralis.health', label: 'Open the clinician demo' },
    video: '/sitemap/videos/clinician.webm',
    captions: '/sitemap/videos/clinician.vtt',
    steps: [
      {
        title: 'Named sign-in with a full audit trail',
        icon: 'fa-user-check',
        what: 'Each clinician signs in under their own name. Every sign-in, record view, result release and change is written to an audit log with who, what and when.',
        why: 'Accountability and traceability are prerequisites for NHS information governance.',
        route: '/clinician',
        image: '/sitemap/clinician-01-signin.jpg',
      },
      {
        title: 'Triage dashboard',
        icon: 'fa-table-list',
        what: 'The dashboard lists the caseload with each patient’s stage, dominant symptom pattern and triage status (not reviewed → in review → reviewed). A “Recent lab uploads” panel shows the latest activity across all patients.',
        why: 'Lets clinicians prioritise who needs attention first instead of working through a flat list.',
        route: '/clinician',
        image: '/sitemap/clinician-02-dashboard.jpg',
      },
      {
        title: 'Add a patient',
        icon: 'fa-user-plus',
        what: 'Clinicians create patient records directly — name, date of birth, stage, concerns, optional contact details. Email is optional, so pseudonymised pilot patients are supported. Details can be corrected later with every change audited.',
        why: 'Results can be filed against the right person from day one, without waiting for the patient to self-register.',
        route: '/clinician',
        image: '/sitemap/clinician-03-add-patient.jpg',
      },
      {
        title: 'Upload lab results — AI reads the document',
        icon: 'fa-cloud-arrow-up',
        what: 'Upload a PDF or a photo of a blood test. The AI reads it and pre-fills the biomarker values. The clinician checks every value against the original, corrects anything, then saves. The file itself is never stored — only the verified numbers and a log of who uploaded what and when.',
        why: 'Removes manual transcription and its errors while keeping the clinician firmly as the final check.',
        route: '/clinician',
        image: '/sitemap/clinician-04-upload.jpg',
      },
      {
        title: 'Patient record — control what the patient sees',
        icon: 'fa-folder-open',
        what: 'The electronic patient record shows every results panel with its visibility status. New results are held by default. The clinician can release a panel, hold back individual values with one tap, and attach a note the patient sees alongside the results.',
        why: 'Sensitive or ambiguous findings can be discussed in person first, protecting the patient from unnecessary worry.',
        route: '/clinician',
        image: '/sitemap/clinician-05-record.jpg',
      },
      {
        title: 'Clinical review & plan',
        icon: 'fa-stethoscope',
        what: 'For each patient the clinician records the primary driver, secondary contributors, treatment plan components and follow-up interval, updates triage status, sends messages and sets reminders. A PDF meeting pack can be generated for the consultation.',
        why: 'Captures clinical reasoning in a structured, shareable way — versioned, so earlier decisions are never lost.',
        route: '/clinician',
        image: '/sitemap/clinician-06-review.jpg',
      },
      {
        title: 'Schedule & booking approvals',
        icon: 'fa-calendar-days',
        what: 'Clinicians publish the dates and times they are available. Patients can only request those slots. Each request appears for approval; approving confirms the appointment, declining reopens the slot.',
        why: 'The clinician’s diary stays accurate and under their control, with zero administrative overhead.',
        route: '/clinician/schedule',
        image: '/sitemap/clinician-07-schedule.jpg',
      },
    ],
  },
  admin: {
    key: 'admin',
    label: 'Admin',
    icon: 'fa-user-shield',
    color: '#14B8A6',
    tagline: 'Oversight, governance and the audit trail — in one place.',
    summary:
      'Administrators and super-administrators monitor adoption and engagement, manage patients and staff, assign clinicians to patients, and review the complete audit log that underpins information-governance compliance.',
    demo: { email: 'charles@superadmin.auralis.health', label: 'Open the super admin demo' },
    video: '/sitemap/videos/admin.webm',
    captions: '/sitemap/videos/admin.vtt',
    steps: [
      {
        title: 'Service overview',
        icon: 'fa-chart-pie',
        what: 'Live counts of total users, onboarded patients, check-ins completed and assessments taken give an at-a-glance picture of adoption and engagement.',
        why: 'Funders and service leads can see real usage, not just sign-ups.',
        route: '/admin',
        image: '/sitemap/admin-01-overview.jpg',
      },
      {
        title: 'Patients & staff management',
        icon: 'fa-users-gear',
        what: 'A searchable directory of every account with role, stage and onboarding status. Super admins can create new clinician, admin and super-admin accounts.',
        why: 'Controlled access — only authorised staff hold clinical or administrative roles.',
        route: '/admin',
        image: '/sitemap/admin-02-users.jpg',
      },
      {
        title: 'Assign clinicians to patients',
        icon: 'fa-link',
        what: 'Super admins link each patient to their responsible clinician so caseloads are explicit and nobody falls through the gaps.',
        why: 'Clear ownership of every patient is a basic safety requirement.',
        route: '/superadmin',
        image: '/sitemap/admin-03-assign.jpg',
      },
      {
        title: 'Audit log',
        icon: 'fa-clipboard-list',
        what: 'A time-stamped record of every sign-in, record view, result upload, release/hold decision, patient edit and appointment decision — filterable by action and resource.',
        why: 'Supports DSPT / UK GDPR accountability and makes any incident fully reconstructable.',
        route: '/admin',
        image: '/sitemap/admin-04-audit.jpg',
      },
      {
        title: 'Consent & data governance',
        icon: 'fa-shield-halved',
        what: 'Consent records are stored per patient with type, status and timestamp. Lab documents are processed in memory and never stored. Patients can request deletion. The whole site sits behind an access gate during piloting.',
        why: 'Privacy-by-design is built into the architecture, not bolted on.',
        route: '/admin',
        image: '/sitemap/admin-05-governance.jpg',
      },
    ],
  },
};
